import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

/**
 * Champ Destinataire(s) avec autocomplétion sur les contacts de l'agence
 * (nom, société, email) ET saisie d'email libre. Multi-destinataires (chips).
 * value : [{ email, contact_id|null, label }]
 * onChange(nextValue)
 */
const RecipientsInput = ({ value = [], onChange, testId = 'recipients-input' }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const resp = await axios.get(`${API}/wizard/contacts/search`, {
          params: { query: q, limit: 8 },
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        setResults((resp.data?.contacts || []).filter((c) => c.email && isValidEmail(c.email)));
        setOpen(true);
      } catch (_) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query]);

  const exists = (email) => value.some((r) => r.email.toLowerCase() === email.toLowerCase());

  const addRecipient = (rcpt) => {
    if (!rcpt.email || exists(rcpt.email)) return;
    onChange([...value, rcpt]);
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  const addContact = (c) => addRecipient({ email: c.email, contact_id: c.id, label: c.display_name || c.email });

  const commitFreeEmails = () => {
    const parts = query.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
    let added = false;
    parts.forEach((p) => {
      if (isValidEmail(p) && !exists(p)) {
        addRecipient({ email: p, contact_id: null, label: p });
        added = true;
      }
    });
    if (added) setQuery('');
  };

  const onKeyDown = (e) => {
    if ((e.key === 'Enter' || e.key === ',' || e.key === ';') ) {
      e.preventDefault();
      commitFreeEmails();
    } else if (e.key === 'Backspace' && !query && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  };

  const removeAt = (i) => onChange(value.filter((_, idx) => idx !== i));

  return (
    <div className="relative" ref={boxRef} data-testid={testId}>
      <div className="w-full px-2.5 py-2 border-2 border-slate-200 rounded-lg focus-within:border-blue-500 flex flex-wrap gap-1.5 items-center min-h-[44px]">
        {value.map((r, i) => (
          <span
            key={`${r.email}-${i}`}
            data-testid={`recipient-chip-${i}`}
            className={`inline-flex items-center gap-1 pl-2 pr-1 py-1 rounded-full text-xs font-medium ${r.contact_id ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-700'}`}
            title={r.contact_id ? `Contact : ${r.label}` : 'Email libre'}
          >
            {r.contact_id && <i className="bi bi-person-fill text-[10px]" />}
            <span className="max-w-[180px] truncate">{r.label || r.email}</span>
            <button type="button" onClick={() => removeAt(i)} className="hover:bg-black/10 rounded-full w-4 h-4 flex items-center justify-center" data-testid={`recipient-remove-${i}`}>
              <i className="bi bi-x text-xs" />
            </button>
          </span>
        ))}
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={commitFreeEmails}
          placeholder={value.length === 0 ? 'Nom, société ou email…' : ''}
          data-testid={`${testId}-field`}
          className="flex-1 min-w-[140px] outline-none text-sm bg-transparent py-1"
        />
      </div>
      {open && (results.length > 0 || loading) && (
        <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-xl z-[80] max-h-60 overflow-y-auto" data-testid={`${testId}-dropdown`}>
          {loading && <div className="px-3 py-2 text-xs text-slate-400"><i className="bi bi-arrow-repeat animate-spin mr-1" />Recherche…</div>}
          {results.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => addContact(c)}
              data-testid={`recipient-option-${c.id}`}
              className="w-full text-left px-3 py-2 hover:bg-blue-50 flex items-center gap-2 text-sm border-b border-slate-50 last:border-0"
            >
              <i className="bi bi-person-circle text-blue-400" />
              <span className="flex-1 min-w-0">
                <span className="font-medium text-slate-800 block truncate">{c.display_name || `${c.prenom} ${c.nom}`.trim()}</span>
                <span className="text-xs text-slate-400 block truncate">{c.email}</span>
              </span>
            </button>
          ))}
        </div>
      )}
      <p className="text-[11px] text-slate-400 mt-1">Recherchez un contact ou saisissez un email libre (Entrée ou virgule pour valider).</p>
    </div>
  );
};

export default RecipientsInput;
